// LeaderboardPage.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, CircularProgress,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { motion } from 'framer-motion';
import BasicVideoPlayer from '../components/BasicVideoPlayer';
import BasicVideoModal from '../components/BasicVideoModal';
import api from '../api/axios';

const MotionBox = motion(Box);
const EASE = [0.22, 1, 0.36, 1];

const MEDALS = ['\u{1F947}', '\u{1F948}', '\u{1F949}'];
const MEDAL_COLORS = ['#C9922A', '#9CA3AF', '#CD7F32'];

// Podium staircase offsets (mb = raise from baseline): 1st highest, 2nd medium, 3rd lowest
const PODIUM_MB = [8, 4, 0];
const PODIUM_WIDTHS = [{ xs: '92vw', sm: 310 }, { xs: '92vw', sm: 265 }, { xs: '92vw', sm: 265 }];

const PodiumCard = ({ nomination, rank, onClick, animDelay }) => {
  const isFirst = rank === 0;
  return (
    <MotionBox
      initial={{ opacity: 0, y: 36 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.75, ease: EASE, delay: animDelay }}
      sx={{ mb: { xs: 0, md: PODIUM_MB[rank] }, width: PODIUM_WIDTHS[rank] }}
    >
      <Paper
        elevation={0}
        onClick={onClick}
        sx={{
          border: '1px solid',
          borderColor: isFirst ? 'rgba(201,146,42,0.5)' : 'rgba(120,100,80,0.13)',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          cursor: 'pointer',
          transition: 'transform 0.22s ease, box-shadow 0.22s ease',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: isFirst
              ? '0 16px 48px rgba(201,146,42,0.18)'
              : '0 10px 32px rgba(0,0,0,0.09)',
          },
          ...(isFirst && { boxShadow: '0 4px 20px rgba(201,146,42,0.1)' }),
          width: '100%',
        }}
      >
        {/* Rank badge */}
        <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: isFirst ? '1.7rem' : '1.3rem', lineHeight: 1 }}>
            {MEDALS[rank]}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: MEDAL_COLORS[rank], fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.1em' }}
          >
            #{rank + 1} PLACE
          </Typography>
        </Box>

        {/* Thumbnail */}
        <Box sx={{ aspectRatio: '16/9', bgcolor: '#0A0A0A', overflow: 'hidden' }}>
          <BasicVideoPlayer
            s3VideoUrl={nomination.s3_video_url}
            thumbnailUrl={nomination.thumbnail_url}
            redditUrl={nomination.reddit_url}
            controls={false}
            autoPlay={false}
          />
        </Box>

        {/* Info */}
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mb: 0.25, fontSize: isFirst ? '1rem' : '0.9rem', lineHeight: 1.3 }}
          >
            {nomination.player_name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 1.5, lineHeight: 1.45 }}
          >
            {nomination.match}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
            <Typography
              sx={{ fontWeight: 800, color: MEDAL_COLORS[rank], fontSize: isFirst ? '1.45rem' : '1.15rem', lineHeight: 1 }}
            >
              {nomination.vote_count}
            </Typography>
            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              votes
            </Typography>
          </Box>
        </Box>

        {/* Gold accent bar on 1st place */}
        {isFirst && (
          <Box
            sx={{
              height: 3,
              background: 'linear-gradient(90deg, transparent, rgba(201,146,42,0.7), transparent)',
            }}
          />
        )}
      </Paper>
    </MotionBox>
  );
};

const LeaderboardPage = () => {
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const openModal = (nom) => { setSelected(nom); setModalOpen(true); };

  const fetchLeaderboardData = async () => {
    try {
      const { data } = await api.get('/api/nominations/?ordering=-vote_count');
      setNominations(data.sort((a, b) => b.vote_count - a.vote_count));
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboardData();
    const interval = setInterval(fetchLeaderboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress size={36} sx={{ color: 'primary.main' }} />
    </Box>
  );

  const top3 = nominations.slice(0, 3);
  const rest = nominations.slice(3);

  // Render order: 2nd left, 1st centre, 3rd right — classic podium
  const podiumOrder = [1, 0, 2];
  // Stagger delays: 2nd and 3rd animate in slightly before 1st to build up
  const podiumDelays = { 1: 0.08, 0: 0.22, 2: 0.14 };

  return (
    <Box sx={{ pt: 10 }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box sx={{ width: 20, height: 2, bgcolor: 'primary.main', borderRadius: '2px', flexShrink: 0 }} />
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.1em', fontSize: '0.7rem' }}>
              25/26 Season
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 7,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Leaderboard
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {nominations.length} nomination{nominations.length !== 1 ? 's' : ''} &nbsp;&middot;&nbsp;{' '}
              {nominations.reduce((s, n) => s + n.vote_count, 0).toLocaleString()} total votes
            </Typography>
          </Box>
        </MotionBox>

        {/* ── Podium — centred, staircase heights ─────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2, md: 3 },
            mb: 8,
            alignItems: { xs: 'stretch', md: 'flex-end' },
            justifyContent: 'center',
          }}
        >
          {podiumOrder.map((rank) =>
            top3[rank] ? (
              <PodiumCard
                key={rank}
                nomination={top3[rank]}
                rank={rank}
                onClick={() => openModal(top3[rank])}
                animDelay={podiumDelays[rank]}
              />
            ) : null
          )}
        </Box>

        {/* ── CTA banner ──────────────────────────────────────────────────── */}
        <MotionBox
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.4 }}
          sx={{
            mb: 7,
            p: 3,
            border: '1px solid rgba(201,146,42,0.18)',
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, rgba(201,146,42,0.04) 0%, transparent 60%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Voting is still open
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              The competition runs until the end of the season.
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            startIcon={<HowToVoteIcon />}
            sx={{
              bgcolor: 'primary.main',
              color: '#111',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
              '&:hover': { bgcolor: 'primary.light' },
            }}
          >
            Vote Now
          </Button>
        </MotionBox>

        {/* ── Full rankings table ──────────────────────────────────────────── */}
        {rest.length > 0 && (
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.5 }}
          >
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', letterSpacing: '0.1em', fontSize: '0.68rem', display: 'block', mb: 2 }}
            >
              Full Rankings
            </Typography>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: '1px solid rgba(120,100,80,0.12)', borderRadius: 2.5 }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        color: 'text.secondary',
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        borderBottom: '1px solid rgba(120,100,80,0.12)',
                        py: 1.75,
                      },
                    }}
                  >
                    <TableCell>Rank</TableCell>
                    <TableCell>Player</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Match</TableCell>
                    <TableCell align="right">Votes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rest.map((nom, i) => (
                    <TableRow
                      key={nom.id}
                      onClick={() => openModal(nom)}
                      sx={{
                        '& td': { borderBottom: '1px solid rgba(120,100,80,0.06)', py: 1.5 },
                        '&:last-child td': { borderBottom: 'none' },
                        '&:hover': { bgcolor: 'rgba(201,146,42,0.04)', cursor: 'pointer' },
                        transition: 'background 0.15s',
                      }}
                    >
                      <TableCell sx={{ color: 'text.disabled', fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums', width: 64 }}>
                        #{i + 4}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{nom.player_name}</TableCell>
                      <TableCell
                        sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'table-cell' }, fontSize: '0.82rem' }}
                      >
                        {nom.match}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {nom.vote_count}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </MotionBox>
        )}
      </Container>

      {selected && (
        <BasicVideoModal open={modalOpen} onClose={() => setModalOpen(false)} nomination={selected} />
      )}
    </Box>
  );
};

export default LeaderboardPage;
